// Piani e funzionalita' (P3): funzioni pure condivise dal pannello Super Admin e da "Il mio piano".
// Nessuna chiamata di rete qui: la UI configura, il backend e' l'unica autorita'.

export const CATEGORY_LABEL = {
  bookings: "Prenotazioni e agenda",
  structure: "Struttura dell'attività",
  ai: "Segretaria AI e WhatsApp",
  messaging: "Chat e messaggi",
  integrations: "Integrazioni",
};
export const CATEGORY_ORDER = ["bookings", "structure", "ai", "messaging", "integrations"];

export const POLICY_LABEL = {
  at_renewal: "Al rinnovo (consigliata)",
  immediate: "Subito, per tutti",
  new_customers_only: "Solo nuovi clienti",
};
export const POLICY_HELP = {
  at_renewal: "I clienti attuali restano alla versione precedente fino al rinnovo.",
  immediate: "La modifica vale subito anche per chi è già abbonato.",
  new_customers_only: "I clienti attuali non cambiano finché non vengono spostati a mano.",
};

export const REASON_LABEL = {
  not_available: "Non inclusa nel piano",
  quota_exhausted: "Quota del periodo esaurita",
  limit_reached: "Limite del piano raggiunto",
  temporarily_disabled: "Temporaneamente disabilitata",
  subscription_expired: "Abbonamento scaduto",
  config_error: "Errore di configurazione",
  allowed: "Consentita",
};

export const STATUS_LABEL = { active: "Attivo", inactive: "Disattivato", archived: "Archiviato", trialing: "In prova" };

// Euro <-> centesimi: gli importi sono SEMPRE interi in centesimi nel database.
export function eurosToCents(text) {
  if (text === null || text === undefined) return null;
  const raw = String(text).trim().replace(",", ".");
  if (raw === "") return null;
  if (!/^\d+(\.\d{1,2})?$/.test(raw)) return NaN;
  return Math.round(parseFloat(raw) * 100);
}

export function centsToEuros(cents) {
  if (cents === null || cents === undefined || cents === "") return "";
  const n = Number(cents) / 100;
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(".", ",");
}

export function formatEuro(cents) {
  if (cents === null || cents === undefined) return "—";
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: Number(cents) % 100 === 0 ? 0 : 2 }).format(Number(cents) / 100);
}

// Raggruppa il catalogo per categoria, nell'ordine di presentazione.
export function groupCatalog(features) {
  const by = {};
  for (const f of features || []) (by[f.category] = by[f.category] || []).push(f);
  const keys = [...CATEGORY_ORDER.filter((k) => by[k]), ...Object.keys(by).filter((k) => !CATEGORY_ORDER.includes(k))];
  return keys.map((k) => ({ category: k, label: CATEGORY_LABEL[k] || k, items: by[k] }));
}

// Stato di modifica di un piano nel form: mappa completa {chiave: {allowed, limit(testo), temporarily_disabled}}.
export function featuresToForm(catalog, planFeatures) {
  const out = {};
  for (const f of catalog || []) {
    const p = (planFeatures || {})[f.key] || {};
    out[f.key] = { allowed: !!p.allowed, limit: p.limit === null || p.limit === undefined ? "" : String(p.limit), temporarily_disabled: !!p.temporarily_disabled };
  }
  return out;
}

// Dal form al payload: limite vuoto = nessun limite; le funzioni on/off non hanno mai limite.
export function formToFeatures(catalog, form) {
  const out = {};
  for (const f of catalog || []) {
    const v = (form || {})[f.key];
    if (!v) continue;
    let limit = null;
    if (f.kind !== "boolean" && String(v.limit).trim() !== "") {
      limit = Number(v.limit);
      if (!Number.isInteger(limit) || limit < 0) throw new Error(`${f.label}: il limite deve essere un numero intero ≥ 0`);
    }
    out[f.key] = { allowed: !!v.allowed, limit, temporarily_disabled: !!v.temporarily_disabled };
  }
  return out;
}

// Quali funzioni cambiano tra due versioni del piano (per l'anteprima prima di salvare).
export function diffFeatures(before, after) {
  const changes = [];
  for (const key of Object.keys(after || {})) {
    const a = (before || {})[key] || { allowed: false, limit: null, temporarily_disabled: false };
    const b = after[key];
    if (a.allowed !== b.allowed || (a.limit ?? null) !== (b.limit ?? null) || !!a.temporarily_disabled !== !!b.temporarily_disabled) changes.push(key);
  }
  return changes;
}

export function planPayloadFromForm(catalog, f) {
  const monthly = eurosToCents(f.price_monthly);
  const annual = eurosToCents(f.price_annual);
  if (Number.isNaN(monthly) || Number.isNaN(annual)) throw new Error("Prezzo non valido: usa euro con al massimo due decimali");
  return {
    name: (f.name || "").trim(),
    description: (f.description || "").trim(),
    price_monthly_cents: monthly,
    price_annual_cents: annual,
    sort_order: Number(f.sort_order) || 100,
    features: formToFeatures(catalog, f.features),
  };
}

// Messaggio d'errore leggibile dalle risposte del backend ({detail:{code,message}} oppure stringa).
export function apiError(e, fallback = "Operazione non riuscita") {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d && typeof d === "object" && d.message) return d.message;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return fallback;
}

// "Il mio piano": una funzione e' bloccata se il backend la segna come tale; il frontend non decide nulla.
export function lockedKeys(features) {
  return new Set((features || []).filter((f) => f.locked).map((f) => f.key));
}

export function usageText(f) {
  if (f.limit === null || f.limit === undefined) return f.kind === "boolean" ? null : "Nessun limite commerciale";
  const unit = f.unit ? ` ${f.unit}` : "";
  if (f.used !== null && f.used !== undefined) return `${f.used} / ${f.limit}${unit}`;
  return f.period ? `${f.limit}${unit} per periodo` : `${f.limit}${unit}`;
}

export function isEntitlementsPayload(data) {
  return !!data && typeof data === "object" && !Array.isArray(data) && Array.isArray(data.features) && "plan" in data;
}


// P5/P6 ────────────────────────────────────────────────────────────────────────────────────────────
export const ENFORCEMENT_LABEL = {
  enforced: "Applicata dal sistema",
  ui_only: "Solo nell'interfaccia (i dati restano accessibili)",
  not_enforced: "Funzione tecnica: non bloccata",
};
export const EXPIRY_LABEL = {
  expire: "La prova scade: le funzioni del piano si bloccano, i dati restano",
  fallback_plan: "Alla scadenza passa a un piano di ripiego",
};
export const SOURCE_LABEL = { admin: "assegnato a mano", trial: "prova gratuita", trial_expired: "ripiego dopo la prova", legacy_default: "piano storico", stripe: "pagamento" };

// Avviso della prova in scadenza (null = nessun avviso). `days_left` arriva dal backend.
export function trialNotice(trial) {
  if (!trial) return null;
  if (trial.expired) return { tone: "danger", text: "La tua prova gratuita è terminata." };
  if (trial.days_left <= 7) return { tone: "warning", text: `La tua prova gratuita termina tra ${trial.days_left} ${trial.days_left === 1 ? "giorno" : "giorni"}.` };
  return null;
}

export function afterExpiryText(trial) {
  const a = trial?.after_expiry;
  if (!a) return "";
  if (a.action === "fallback_plan" && a.fallback_plan) return `Alla scadenza passerai al piano ${a.fallback_plan}: non perdi dati né prenotazioni.`;
  return "Alla scadenza le funzioni del piano si bloccano: dati, prenotazioni e conversazioni restano al sicuro.";
}

// Le funzioni sono davvero bloccate (e quindi i lucchetti sono "veri") solo se il backend dice che l'applicazione vale per questa attività.
export function enforcementApplies(data) {
  return !!data?.enforcement?.applies;
}

// Notifiche dei blocchi dal backend: il 403 porta X-Plan-Block-Reason / X-Plan-Block-Feature.
export function planBlockFrom(error) {
  const h = error?.response?.headers || {};
  const reason = h["x-plan-block-reason"];
  return reason ? { reason, feature: h["x-plan-block-feature"] || null, message: typeof error.response.data?.detail === "string" ? error.response.data.detail : "" } : null;
}

export function parseNotifyDays(text) {
  const parts = String(text || "").split(/[ ,;]+/).filter(Boolean);
  const nums = parts.map((x) => Number(x));
  if (nums.some((n) => !Number.isInteger(n) || n < 0 || n > 60)) throw new Error("Giorni di avviso: numeri interi tra 0 e 60");
  return [...new Set(nums)].sort((a, b) => b - a);
}

// "Il mio piano": altri piani, richiesta di upgrade, quote residue. Nessuna decisione qui: i dati e le regole arrivano dal backend.
export const BILLING_LABEL = { monthly: "Mensile", annual: "Annuale" };

export function planMonthlyPrice(plan, billing) {
  const v = billing === "annual" ? plan?.price_annual_cents : plan?.price_monthly_cents;
  return v === null || v === undefined ? null : v;
}

export function formatPlanPrice(plan, billing) {
  const v = planMonthlyPrice(plan, billing);
  return v === null ? "Prezzo su richiesta" : `${formatEuro(v)} al mese`;
}

// Risparmio % del piano annuale rispetto al mensile (null se non calcolabile o non conveniente).
export function annualSavingPercent(plan) {
  const m = plan?.price_monthly_cents;
  const a = plan?.price_annual_cents;
  if (!m || a === null || a === undefined || a >= m) return null;
  return Math.round((1 - a / m) * 100);
}

export function pendingFor(data, slug) {
  return (data?.upgrade?.pending || []).find((p) => p.plan_slug === slug) || null;
}

// Etichetta e stato del pulsante di un piano, in base alla relazione col piano attuale decisa dal backend.
export function upgradeAction(plan, data) {
  const pending = pendingFor(data, plan.slug);
  const trialing = data?.plan?.status === "trialing";
  if (pending) return { label: "Richiesta inviata", kind: "pending", disabled: true, pending };
  switch (plan.relation) {
    case "current": return { label: "Il tuo piano", kind: "current", disabled: true };
    case "renew": return { label: trialing ? `Continua con ${plan.name}` : `Rinnova ${plan.name}`, kind: "renew", disabled: false };
    case "upgrade": return { label: `Passa a ${plan.name}`, kind: "upgrade", disabled: false };
    case "downgrade": return { label: `Richiedi il passaggio a ${plan.name}`, kind: "downgrade", disabled: false };
    default: return { label: `Richiedi il piano ${plan.name}`, kind: "alternative", disabled: false };
  }
}

export function formatDateIt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("it-IT", { day: "numeric", month: "long" });
}

// «Ne restano 297 su 300 · si azzera il 12 novembre» (null se non misurabile).
export function remainingText(f) {
  if (f.remaining === null || f.remaining === undefined || f.limit === null || f.limit === undefined) return null;
  const unit = f.unit ? ` ${f.unit}` : "";
  const est = f.usage_source === "simulated" ? " (stima)" : "";
  const reset = f.resets_at ? ` · si azzera il ${formatDateIt(f.resets_at)}` : "";
  return `Ne restano ${f.remaining} su ${f.limit}${unit}${est}${reset}`;
}

// Cella del confronto: «—» non inclusa, «✔» inclusa, numero = limite, «Illimitato».
export function comparisonCell(meta, planFeature) {
  if (!planFeature || !planFeature.included) return "—";
  if (meta?.kind === "boolean") return "✔";
  return planFeature.limit === null || planFeature.limit === undefined ? "Illimitato" : String(planFeature.limit);
}

export function upgradePayload({ plan_slug, billing = "monthly", feature_key, note }) {
  const out = { plan_slug, billing };
  if (feature_key) out.feature_key = feature_key;
  const n = (note || "").trim();
  if (n) out.note = n.slice(0, 500);
  return out;
}

// Legge dal link del lucchetto (?richiedi=pro&funzione=voice_transcription) quale piano/funzione precompilare.
export function requestFromSearch(search) {
  const p = new URLSearchParams(search || "");
  return { plan: p.get("richiedi") || null, feature: p.get("funzione") || null };
}

export const UPGRADE_ERRORS = {
  already_on_plan: "È già il tuo piano.",
  plan_not_active: "Questo piano non è al momento disponibile.",
  plan_not_found: "Questo piano non esiste più: ricarica la pagina.",
};


// Disdetta a fine periodo («Il mio piano»). Il backend decide cosa e' consentito (can_cancel / can_revoke); qui solo i testi.
export function formatDateLong(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });
}

export function cancellationInfo(c, planName) {
  if (!c || !c.access_until) return null;
  const until = formatDateLong(c.access_until);
  const next = c.next_plan?.name || null;
  if (c.requested) {
    return { tone: "warning", title: `Disdetta richiesta: ${planName || "il tuo piano"} resta attivo fino al ${until}.`,
      text: next ? `Dopo questa data passerai al piano ${next}. Non perderai prenotazioni, clienti né configurazioni.` : "Dopo questa data il piano cambierà.", until, next };
  }
  return { tone: "info", title: `${planName || "Il tuo piano"} è attivo fino al ${until}.`,
    text: next ? `Se non rinnovi, dopo questa data passerai al piano ${next}, senza perdere prenotazioni, clienti né configurazioni.` : "", until, next };
}

export const CANCEL_REASON_TEXT = {
  trial: "La prova gratuita termina da sola alla sua scadenza.",
  period_ended: "Il periodo è già terminato.",
  no_period_end: "Il tuo piano non ha una scadenza da disdire.",
  no_base_plan: "La disdetta non è ancora disponibile: contatta l'assistenza.",
  already_base: "Sei già sul piano base.",
  legacy: "",
};
